'use client';

import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import { DelegateInfo } from '@/app/types/delegates';
import { userDataAtom } from '@/app/atoms/userData';
import { proposalThresholdAtom } from '@/app/atoms/governorContract';
import { UserData } from '@/app/types/userData';
import { formatUnits } from 'ethers';
import { formatVotingPower } from '../helpers/formatVotingPower';

// ---- base atoms ----
export const delegatesAtom = atom<DelegateInfo[]>([]);

export const delegatorsAtom = atom<{ address: `0x${string}` | null }>({
  address: null,
});

export const userDataUpdateAtom = atom<UserData | null>((get) => {
  const baseUserData = get(userDataAtom);
  const delegates = get(delegatesAtom);
  const proposalThreshold = get(proposalThresholdAtom);
  if (!proposalThreshold) return baseUserData;

  const userAddress = baseUserData.address;
  const userDelegate = delegates.find((d) => d.address === userAddress);
  if (!userDelegate) return baseUserData;

  const readableVotingPower = formatUnits(userDelegate.votingPower || '0', 18);
  const formattedVotingPower = formatVotingPower(readableVotingPower) || '0';

  const meetsProposalThreshold =
    Number(formattedVotingPower.replace(/,/g, '')) >=
    Number(proposalThreshold.replace(/,/g, ''));

  return {
    ...baseUserData,
    address: userAddress,
    delegateCount: userDelegate.delegators ? 1 : 0,
    delegatesAddress: userDelegate.delegators || [],
    delegatorsAddresses: delegates
      .filter((d) => d.delegators?.includes(userAddress))
      .map((d) => d.address),
    delegatorsCount: (userDelegate.delegators || []).length,
    isUserSelfDelegated: (userDelegate.delegators || []).includes(userAddress),
    votingPower: Number(formattedVotingPower).toLocaleString(),
    meetsProposalThreshold,
    delegatedTokens: formattedVotingPower,
    userHasVoted: false,
  };
});

export const sortedDelegateAtom = atom<DelegateInfo[]>((get) => {
  const delegates = get(delegatesAtom);
  const userData = get(userDataAtom);
  const address = userData.address;

  return [...delegates].sort((a, b) => {
    const isA = a.delegators?.includes(address as `0x${string}`);
    const isB = b.delegators?.includes(address as `0x${string}`);
    if (isA && !isB) return -1;
    if (!isA && isB) return 1;
    return 0;
  });
});

// ---- async atom (unchanged) ----
export const totalDelegationPowerAtom = atom(async () => {
  const url = process.env.NEXT_PUBLIC_TOTAL_DELEGATED_API;
  if (!url) {
    console.warn('TOTAL_DELEGATED_API missing');
    return 0;
  }

  let text: string;
  try {
    const res = await fetch(url, {
      headers: { accept: 'application/json,text/plain;q=0.9' },
      cache: 'no-store',
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      console.warn(`totalDelegated fetch failed ${res.status}: ${body.slice(0, 160)}`);
      return 0;
    }
    text = await res.text();
  } catch (e) {
    console.warn('totalDelegated network error:', e);
    return 0;
  }

  const trimmed = text.trim();

  let raw: string | number | undefined;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const json = JSON.parse(trimmed);
      raw = json.totalDelegated ?? json.total_delegated ?? json.total ?? json.value;
    } catch {
      /* fall through */
    }
  }
  if (raw === undefined) {
    const digits = trimmed.replace(/[^0-9]/g, '');
    if (!digits) {
      console.warn(
        `totalDelegated: no numeric content in response: "${trimmed.slice(0, 160)}"`,
      );
      return 0;
    }
    raw = digits;
  }

  let wei: bigint;
  try {
    wei = BigInt(String(raw));
  } catch (e) {
    console.error('Failed to parse total delegation power to BigInt:', e);
    return 0;
  }

  try {
    const readable = formatUnits(wei, 18);
    const num = parseFloat(readable);
    if (!Number.isFinite(num)) return 0;
    return Math.round(num * 100) / 100;
  } catch (e) {
    console.error('Failed to scale total delegation power:', e);
    return 0;
  }
});

// ---- loadable wrapper for Client Components (Option A) ----
export const totalDelegationPowerLoadable = loadable(totalDelegationPowerAtom);
